import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Drop } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { PinnedNotice } from "@/components/PinnedNotice";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function WellbeingPublic() {
  const [board, setBoard] = useState(null);
  const [card, setCard] = useState(null);

  useEffect(() => {
    api.get("/wellbeing/public").then(({ data }) => setBoard(data)).catch(() => setBoard({ breaks: [], plans: [] }));
    api.get("/wellbeing/break-card").then(({ data }) => setCard(data)).catch(() => setCard(null));
  }, []);

  useSeo({
    title: "Breaks, naps and sleep therapy at work | Unity & Utility",
    description: (board?.blurb || "Restroom, water, a twenty-minute nap and a stretch through the working day, plus a sleep therapy plan.").slice(0, 180),
    path: "/wellbeing",
    image: board?.image_url ? `${BACKEND}${board.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Wellbeing", "/wellbeing"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-4xl px-5 py-10 sm:px-8 sm:py-14" data-testid="wellbeing-public-page">
        <Crumbs items={[["Home", "/"], ["Wellbeing", "/wellbeing"]]} />
        <p className="overline text-[#B3241B]">Breaks that are actually taken</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Drop size={34} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Breaks, naps &amp; therapy
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="wellbeing-blurb">
          {board?.blurb || "Restroom, water, a twenty-minute nap and a stretch through the working day, plus a sleep therapy plan when you want more than a nap."}
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/wellbeing`} text="Breaks, naps and sleep therapy at work"
            testid="wellbeing-share" instagram />
        </div>

        {board?.image_url && (
          <img data-testid="wellbeing-image" src={`${BACKEND}${board.image_url}`} alt="Our break area"
            className="mt-8 h-56 w-full border border-border object-cover sm:h-72" />
        )}

        <div className="mt-8 grid gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="wellbeing-breaks">
          {(board?.breaks || []).map((b) => (
            <div key={b.key} data-testid={`wellbeing-break-${b.key}`} className="border border-border bg-white">
              {b.image_url && (
                <img src={`${BACKEND}${b.image_url}`} alt={b.label} className="h-32 w-full object-cover" />
              )}
              <div className="p-3">
                <p className="overline text-slate-500">{b.label}</p>
                <p className="mt-1 text-lg font-bold text-[#0F172A]">{b.minutes} min</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-sm text-slate-600" data-testid="wellbeing-water">
          Water target: {board?.water_target || 8} glasses a day.
        </p>

        {card?.day && (
          <section className="mt-10 border border-[#002FA7] bg-white p-5" data-testid="break-card">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="overline text-[#002FA7]">Today&apos;s breaks · {card.day}</p>
              {card.auto_posted && (
                <span data-testid="break-card-posted" className="overline text-[0.6rem] text-[#0B5C2E]">
                  Posted to Facebook
                </span>
              )}
            </div>
            <p className="mt-2 text-sm font-semibold text-[#0F172A]" data-testid="break-card-allowances">
              {(card.breaks || []).map((b) => `${b.label} ${b.minutes} min`).join(" · ")}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="break-card-note">
              {card.note}
            </p>
            <div className="mt-3">
              <ShareBar
                url={card.share_token ? `${window.location.origin}/p/${card.share_token}` : `${BACKEND}/api/share/wellbeing`}
                text={`Today's breaks — ${(card.breaks || []).map((b) => `${b.label} ${b.minutes} min`).join(" · ")}`}
                testid="break-card-share" instagram compact />
            </div>
          </section>
        )}

        <h2 className="mt-10 text-base font-semibold text-[#0F172A] md:text-lg">Sleep therapy plans</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="wellbeing-plans">
          {(board?.plans || []).map((p, i) => (
            <div key={i} data-testid={`wellbeing-plan-${i}`} className="border border-border bg-white p-4">
              <p className="font-semibold text-[#0F172A]">{p.name}</p>
              <p className="mt-1 text-xl font-bold text-[#0B5C2E]">{p.currency} {p.price}<span className="text-xs font-semibold text-slate-500"> / month</span></p>
              <ul className="mt-2 space-y-1 text-sm text-slate-700">
                {(p.includes || []).map((x, n) => <li key={n}>· {x}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <PinnedNotice testid="wellbeing-pinned-notice" />


        <Link to="/rest-sleep-board" data-testid="wellbeing-back"
          className="tap-safe mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
          See the rest &amp; sleep board
        </Link>
      </div>
    </PublicShell>
  );
}
